import React, { lazy, useEffect, useState } from "react";
import "./App.css";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { themeChange } from "theme-change";
import { useDispatch, useSelector } from "react-redux";
import initializeApp from "./app/init";
import { setCredentials, selectCurrentToken } from "./app/authSlice";
import LoadingSpinner from "./components/common/LoadingSpinner";
import SingleMockTest from "./features/mocktests/components/SingleMockTest";
import SpeakingSection from "./features/mocktests/components/SpeakingSection";
import ListeningPage from "./features/mocktests/components/ListeningPage";
import ReadingPage from "./features/mocktests/components/ReadingPage";
import WritingPage from "./features/mocktests/components/WritingPage";
import SpeakingPage from "./features/mocktests/components/SpeakingPage";
import AttemptResultPage from "./features/mocktests/components/AttemptResultPage";

// Importing pages
const Layout = lazy(() => import("./containers/Layout"));
const Login = lazy(() => import("./pages/Login"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Register = lazy(() => import("./pages/Register"));
const Documentation = lazy(() => import("./pages/Documentation"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));


// Initializing different libraries
initializeApp();

// Silently attempts to refresh the access token on page load.
// Shows a spinner while verifying; children render once decided.
function PersistLogin({ children }) {
  const [isVerifying, setIsVerifying] = useState(true);
  const dispatch = useDispatch();
  const token = useSelector(selectCurrentToken);

  useEffect(() => {
    if (token) {
      setIsVerifying(false);
      return;
    }

    const silentRefresh = async () => {
      try {
        const baseUrl =
          process.env.REACT_APP_API_BASE_URL ||
          "https://stagingapi.acadimyst.com/";
        const response = await fetch(
          `${baseUrl}api/v1/en/student/auth/refresh-token`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          }
        );
        if (response.ok) {
          const result = await response.json();
          const newToken = result?.data?.data?.token;
          const maxSessionUntil = result?.data?.data?.maxSessionUntil;
          if (newToken) {
            dispatch(setCredentials({ token: newToken, maxSessionUntil }));
          }
        }
      } catch {
        // Silent fail — user will be directed to login
      } finally {
        setIsVerifying(false);
      }
    };

    silentRefresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isVerifying) {
    return <LoadingSpinner />;
  }

  return children;
}

// Redirects unauthenticated users away from protected routes.
// Public routes are always accessible regardless of token state.
function ProtectedRoute({ children }) {
  const token = useSelector(selectCurrentToken);
  const location = useLocation();

  const publicRoutes = [
    "/login",
    "/forgot-password",
    "/register",
    "/documentation",
    "/payment-callback",
  ];

  const isPublicRoute = publicRoutes.includes(location.pathname);

  if (isPublicRoute) return children;
  if (!token) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  const token = useSelector(selectCurrentToken);

  useEffect(() => {
    // 👆 daisy UI themes initialization
    themeChange(false);
  }, []);

  return (
    <>
      <Router>
        <PersistLogin>
          <Routes>

          
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <Routes>
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/documentation" element={<Documentation />} />
                  <Route path="/payment-callback" element={<PaymentCallback />} />

                  {/* Place new routes over this */}
                  <Route path="/app/*" element={<Layout />} />

                  <Route
                    path="*"
                    element={
                      <Navigate
                        to={token ? "/app/dashboard" : "/login"}
                        replace
                      />
                    }
                  />

                  <Route path="/mock-test/:testId" element={<SingleMockTest />} />

            <Route
              path="/mock-speaking/:testId"
              element={<SpeakingSection />}
            />

            <Route path="/mock-test/:testId" element={<SingleMockTest />} />
            <Route
              path="/mock-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/mock-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/mock-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/mock-test/:testId/speaking"
              element={<SpeakingPage />}
            />
            {/* Attempt Result Route for IELTS */}
            <Route
              path="/mock-test/:testId/attempt/:attemptId/result"
              element={<AttemptResultPage />}
            />

            {/* Direct Speaking Route for IELTS */}
            <Route path="/speaking" element={<SpeakingPage />} />

            {/* Mini Mock Test Routes */}
            <Route
              path="/mini-mock-test/:testId"
              element={<SingleMockTest testType="mini_mock" />}
            />
            <Route
              path="/mini-mock-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/mini-mock-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/mini-mock-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/mini-mock-test/:testId/speaking"
              element={<SpeakingPage />}
            />
            <Route
              path="/mini-mock-test/:testId/attempt/:attemptId/result"
              element={<AttemptResultPage />}
            />

            {/* Class Test Routes */}
            <Route
              path="/class-test/:testId"
              element={<SingleMockTest testType="class_test" />}
            />
            <Route
              path="/class-test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/class-test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/class-test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/class-test/:testId/speaking"
              element={<SpeakingPage />}
            />
            <Route
              path="/class-test/:testId/attempt/:attemptId/result"
              element={<AttemptResultPage />}
            />

            {/* Bundle Test Routes */}
            <Route
              path="/app/bundles/:bundleId/test/:testId/listening"
              element={<ListeningPage />}
            />
            <Route
              path="/app/bundles/:bundleId/test/:testId/reading"
              element={<ReadingPage />}
            />
            <Route
              path="/app/bundles/:bundleId/test/:testId/writing"
              element={<WritingPage />}
            />
            <Route
              path="/app/bundles/:bundleId/test/:testId/speaking"
              element={<SpeakingPage />}
            />
                </Routes>
              </ProtectedRoute>
            }
          />
        </Routes>
        </PersistLogin>
      </Router>
    </>
  );
}

export default App;
